import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Heroicons imports
import {
  HomeIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  UserGroupIcon,
  CubeIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  BanknotesIcon,
  HandRaisedIcon,
  KeyIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = localStorage.getItem("userRole");
  const username = localStorage.getItem("username");

  // Scroll detection for navbar elevation
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(null);
  }, [location]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setDropdownOpen(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleLanguageChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    setIsOpen(false);
    navigate("/login");
  };

  const homeRoute = userRole ? "/home" : "/";

  // Check if route is active
  const isActive = (path) => location.pathname === path;

  // Navigation groups for better organization
  const navGroups = {
    main: [
      {
        path: homeRoute,
        label: t("home"),
        icon: HomeIcon,
        show: true,
      },
      {
        path: "/dashboard",
        label: t("dashboard"),
        icon: ChartBarIcon,
        show: userRole === "admin",
      },
    ],
    operations: [
      {
        path: "/clients",
        label: t("clients"),
        icon: UserGroupIcon,
        show: userRole === "admin" || userRole === "manager" || userRole === "partner",
      },
      {
        path: "/products",
        label: t("products"),
        icon: CubeIcon,
        show: userRole === "admin" || userRole === "manager",
      },
      {
        path: "/delivery",
        label: t("delivery"),
        icon: TruckIcon,
        show: userRole === "admin" || userRole === "manager" || userRole === "driver",
      },
      {
        path: "/production",
        label: t("production"),
        icon: CubeIcon,
        show: userRole === "admin" || userRole === "manager" || userRole === "production_staff",
      },
      {
        path: "/stock-management",
        label: t("stock"),
        icon: CubeIcon,
        show: userRole === "admin" || userRole === "manager",
        badge: "🧂",
      },
    ],
    finance: [
      {
        path: "/ledger",
        label: t("ledger"),
        icon: BookOpenIcon,
        show: userRole === "admin",
      },
      {
        path: "/ledger/client",
        label: t("clientLedger"),
        icon: MagnifyingGlassIcon,
        show: userRole === "admin",
      },
      {
        path: "/accounting",
        label: t("accounting"),
        icon: BanknotesIcon,
        show: userRole === "admin",
      },
      {
        path: "/advances",
        label: t("advancePayments"),
        icon: HandRaisedIcon,
        show: userRole === "admin",
      },
    ],
    admin: [
      {
        path: "/admin/users",
        label: t("userManagement"),
        icon: KeyIcon,
        show: userRole === "admin",
      },
    ],
  };

  // Flatten all nav items for mobile menu
  const allNavItems = [
    ...navGroups.main,
    ...navGroups.operations,
    ...navGroups.finance,
    ...navGroups.admin,
  ].filter((item) => item.show);

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#2D2F5E] shadow-lg"
            : "bg-gradient-to-r from-[#2D2F5E] to-[#3D3F6E]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo & Brand */}
            <Link
              to={homeRoute}
              className="flex items-center space-x-2 sm:space-x-3 group"
            >
              <div className="relative">
                <img
                  src="/logo.png"
                  alt="Pyszna Food ERP"
                  className="h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-110"
                />
                {/* Pulsing dot for online status */}
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full animate-pulse"></div>
              </div>
              <div className="hidden sm:block">
                <div className="text-[#F5E9D8] font-bold text-base sm:text-lg tracking-tight">
                  Pyszna Bakery
                </div>
                <div className="text-[#F76B4C] text-xs font-medium -mt-1">
                  ERP System
                </div>
              </div>
              <div className="sm:hidden text-[#F5E9D8] font-bold text-sm">
                Pyszna
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Main Items */}
              {navGroups.main
                .filter((item) => item.show)
                .map((item) => (
                  <NavLink key={item.path} item={item} isActive={isActive} />
                ))}

              {/* Operations Dropdown */}
              {navGroups.operations.some((item) => item.show) && (
                <DropdownMenu
                  label={t("operations") || "Operations"}
                  items={navGroups.operations.filter((item) => item.show)}
                  isActive={isActive}
                  isOpen={dropdownOpen === "operations"}
                  onToggle={() =>
                    setDropdownOpen(
                      dropdownOpen === "operations" ? null : "operations"
                    )
                  }
                />
              )}

              {/* Finance Dropdown */}
              {navGroups.finance.some((item) => item.show) && (
                <DropdownMenu
                  label={t("finance") || "Finance"}
                  items={navGroups.finance.filter((item) => item.show)}
                  isActive={isActive}
                  isOpen={dropdownOpen === "finance"}
                  onToggle={() =>
                    setDropdownOpen(dropdownOpen === "finance" ? null : "finance")
                  }
                />
              )}

              {/* Admin Items */}
              {navGroups.admin
                .filter((item) => item.show)
                .map((item) => (
                  <NavLink key={item.path} item={item} isActive={isActive} />
                ))}
            </div>

            {/* Right Side - Language, User, Mobile Menu */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Language Selector */}
              <LanguageSelector
                value={i18n.language}
                onChange={handleLanguageChange}
              />

              {/* User Info (Desktop) */}
              {userRole && (
                <div className="hidden lg:flex items-center space-x-2">
                  <div className="bg-[#F5E9D8] bg-opacity-10 px-3 py-1.5 rounded-lg border border-[#F5E9D8] border-opacity-20">
                    <div className="text-[#F5E9D8] text-xs font-medium">
                      {username}
                    </div>
                    <div className="text-[#F76B4C] text-xs capitalize">
                      {userRole}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-[#F5E9D8] hover:text-[#F76B4C] hover:bg-[#F5E9D8] hover:bg-opacity-10 rounded-lg transition-all duration-200"
                    aria-label={t("logout")}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              )}

              {/* Login Button (Desktop, not logged in) */}
              {!userRole && (
                <Link
                  to="/login"
                  className="hidden lg:flex items-center space-x-2 bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span className="font-medium">{t("login")}</span>
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMenu}
                className="lg:hidden p-2 text-[#F5E9D8] hover:bg-[#F5E9D8] hover:bg-opacity-10 rounded-lg transition-all duration-200"
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isOpen}
          navItems={allNavItems}
          isActive={isActive}
          userRole={userRole}
          username={username}
          onLogout={handleLogout}
        />
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

// ==========================================
// COMPONENTS
// ==========================================

function NavLink({ item, isActive }) {
  const Icon = item.icon;
  const active = isActive(item.path);

  return (
    <Link
      to={item.path}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-[#F76B4C] text-white shadow-lg"
          : "text-[#F5E9D8] hover:bg-[#F5E9D8] hover:bg-opacity-10 hover:text-[#F76B4C]"
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{item.label}</span>
      {item.badge && <span className="ml-1">{item.badge}</span>}
    </Link>
  );
}

function DropdownMenu({ label, items, isActive, isOpen, onToggle }) {
  const hasActiveItem = items.some((item) => isActive(item.path));

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          hasActiveItem || isOpen
            ? "bg-[#F76B4C] text-white"
            : "text-[#F5E9D8] hover:bg-[#F5E9D8] hover:bg-opacity-10 hover:text-[#F76B4C]"
        }`}
      >
        <span>{label}</span>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-[#2D2F5E] border-opacity-10 overflow-hidden animate-slideDown">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 text-sm transition-all duration-200 ${
                  active
                    ? "bg-[#F76B4C] text-white"
                    : "text-[#2D2F5E] hover:bg-[#F5E9D8]"
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">{item.label}</span>
                {item.badge && <span className="ml-auto">{item.badge}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LanguageSelector({ value, onChange }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="appearance-none bg-[#F5E9D8] bg-opacity-10 border border-[#F5E9D8] border-opacity-20 rounded-lg px-3 py-2 pr-8 text-[#F5E9D8] text-sm font-medium cursor-pointer hover:bg-opacity-20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
      >
        <option value="en">🇬🇧 EN</option>
        <option value="bn">🇧🇩 BN</option>
      </select>
      <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5E9D8] pointer-events-none" />
    </div>
  );
}

function MobileMenu({ isOpen, navItems, isActive, userRole, username, onLogout }) {
  const { t } = useTranslation();

  return (
    <div
      className={`lg:hidden fixed top-16 left-0 right-0 bottom-0 bg-[#2D2F5E] transform transition-transform duration-300 ease-in-out z-50 overflow-y-auto ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="p-4 space-y-2">
        {/* User Info (Mobile) */}
        {userRole && (
          <div className="bg-[#F5E9D8] bg-opacity-10 border border-[#F5E9D8] border-opacity-20 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[#F5E9D8] font-medium">{username}</div>
                <div className="text-[#F76B4C] text-sm capitalize">{userRole}</div>
              </div>
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition-all duration-200"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="text-sm font-medium">{t("logout")}</span>
              </button>
            </div>
          </div>
        )}

        {/* Nav Items */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                active
                  ? "bg-[#F76B4C] text-white shadow-lg"
                  : "text-[#F5E9D8] hover:bg-[#F5E9D8] hover:bg-opacity-10"
              }`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
              {item.badge && <span className="ml-auto">{item.badge}</span>}
            </Link>
          );
        })}

        {/* Login Button (Mobile, not logged in) */}
        {!userRole && (
          <Link
            to="/login"
            className="flex items-center justify-center space-x-2 bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-3 rounded-lg transition-all duration-200 shadow-lg mt-4"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            <span className="font-medium">{t("login")}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

// Add animation keyframe to your global CSS or index.css:
// @keyframes slideDown {
//   from {
//     opacity: 0;
//     transform: translateY(-10px);
//   }
//   to {
//     opacity: 1;
//     transform: translateY(0);
//   }
// }
// .animate-slideDown {
//   animation: slideDown 0.2s ease-out;
// }